import type { CpiPosition } from '../types/smart-pricing.types';

/**
 * «Frente al mercado» — Etapa 6 Parte A (plan 173, spec 156 decisión #6).
 *
 * El CPI (precio propio ÷ promedio del mercado × 100) baja a dato de fila con
 * nombre en llano. El número siempre estuvo bien; lo que no decía nada era el
 * rótulo: «CPI 112» obliga a saber la fórmula, «12 % más caro que el mercado»
 * no. Se traduce UNA vez acá para que las tres vistas digan lo mismo.
 *
 * El campo `cpi` de la API se queda como está: es un contrato, no copy.
 */

export interface VsMercado {
  /** Puntos porcentuales contra el mercado. Positivo = más caro. */
  pct: number;
  /** Para una celda de tabla: «+12 %». */
  etiqueta: string;
  /**
   * Para una frase: «12 % por encima del mercado».
   *
   * 🔴 Invariable a propósito, no «más caro»/«más barato». La primera versión
   * usaba el adjetivo y la pantalla la mostró con sujeto plural: «Estos 2
   * productos están 23 % más barato que el mercado». «Por encima» y «por debajo»
   * concuerdan con cualquier sujeto, y son las mismas palabras que ya usan los
   * segmentos y los indicadores de arriba.
   */
  frase: string;
  tono: 'caro' | 'igual' | 'barato';
}

/** El encabezado de la columna y el rótulo del dato. */
export const VS_MERCADO_TITULO = 'Frente al mercado';

/**
 * Hasta acá se considera que el precio está en el mercado. Es más angosto que
 * el ±5 % con el que el backend arma las posiciones a propósito: eso agrupa,
 * esto describe una fila, y decir «1 % más caro» de un producto es ruido.
 */
const UMBRAL_IGUAL = 2;

export function describirVsMercado(cpi: number): VsMercado {
  const pct = Math.round(cpi - 100);
  const abs = Math.abs(pct);

  // Menos U+2212, no guion: un «-8 %» con guion corto se lee como separador.
  const etiqueta = pct > 0 ? `+${pct} %` : pct < 0 ? `−${abs} %` : '0 %';

  if (abs <= UMBRAL_IGUAL) {
    return { pct, etiqueta, frase: 'al precio del mercado', tono: 'igual' };
  }

  return pct > 0
    ? { pct, etiqueta, frase: `${abs} % por encima del mercado`, tono: 'caro' }
    : { pct, etiqueta, frase: `${abs} % por debajo del mercado`, tono: 'barato' };
}

/**
 * Las cinco posiciones frente al mercado, en el orden de la escala (pulido,
 * spec 232): de más barato a más caro. El nombre corto y el rango van en la
 * fila de Análisis → Por segmento; la muestra, en la barra de cómo se
 * reparten. Los colores son tokens: en oscuro salen solos.
 */
export const POSICIONES: ReadonlyArray<{
  clave: CpiPosition;
  nombre: string;
  rango: string;
  muestra: string;
}> = [
  { clave: 'VERY_BELOW', nombre: 'Muy bajo', rango: 'más de 15 % por debajo', muestra: 'bg-info' },
  { clave: 'BELOW', nombre: 'Bajo', rango: '5 a 15 % por debajo', muestra: 'bg-info/50' },
  { clave: 'ALIGNED', nombre: 'Alineado', rango: '±5 %', muestra: 'bg-ok' },
  { clave: 'ABOVE', nombre: 'Alto', rango: '5 a 20 % por encima', muestra: 'bg-alerta' },
  {
    clave: 'VERY_ABOVE',
    nombre: 'Muy alto',
    rango: 'más de 20 % por encima',
    muestra: 'bg-bloqueo',
  },
];

/** La posición de una clave; null si no es una de las cinco. */
export const posicionDe = (clave: string) => POSICIONES.find((p) => p.clave === clave) ?? null;
